import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DomainConfigComponent } from '@app/domain-config.component';
import { AuthService } from '@app/login/auth.service';
import { ToastService } from '@flows/shared/toast/toast-service';
import { unreadMessageBadgeCount } from '@app/util/portal-chat-unread-badge';

export interface PortalChatLiveEvent {
  type: string;
  fqdn?: string;
  userId?: number;
  requirementId?: number;
  requirementLabel?: string;
  at?: string;
}

export interface PortalChatUnreadInbox {
  totalUnreadRequirements: number;
  totalUnreadMessages?: number;
  requirements: Array<{
    id: number;
    name: string;
    latestAt: string | null;
    unreadMessageCount?: number;
  }>;
}

const POLL_MS = 120000;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
/** Solo resetear reconnectAttempt si el stream se mantuvo abierto este tiempo. */
const SSE_STABLE_MS = 5000;

/**
 * Canal live del Portal de Comunicación (SSE) + degradación a poll.
 * Solo sesión interna Flows. Flag fail-closed: trackvision.portalChatLive.
 */
@Injectable({ providedIn: 'root' })
export class PortalChatLiveService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly eventsSubject = new Subject<PortalChatLiveEvent>();
  private readonly inboxSubject = new BehaviorSubject<PortalChatUnreadInbox>({
    totalUnreadRequirements: 0,
    requirements: [],
  });
  private readonly unreadCounts = new BehaviorSubject<Map<number, number>>(new Map());

  readonly events$ = this.eventsSubject.asObservable();
  readonly inbox$ = this.inboxSubject.asObservable();
  readonly unreadCounts$ = this.unreadCounts.asObservable();

  private started = false;
  private abortController: AbortController | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private usePollFallback = false;
  private sseOpenedAt = 0;
  private visibilityHandler: (() => void) | null = null;
  private readonly refreshHandlers = new Set<() => void>();

  constructor(
    private domainConfig: DomainConfigComponent,
    private auth: AuthService,
    private toast: ToastService,
  ) {}

  /** Callbacks para refrescar sidebar sin acoplar el servicio al componente. */
  onUnreadSignal(handler: () => void): () => void {
    this.refreshHandlers.add(handler);
    return () => this.refreshHandlers.delete(handler);
  }

  hasUnreadForRequirement(requirementId: number): boolean {
    return this.unreadCountForRequirement(requirementId) > 0;
  }

  unreadCountForRequirement(requirementId: number): number {
    return unreadMessageBadgeCount(this.unreadCounts.value.get(Number(requirementId)));
  }

  start(): void {
    if (this.started) {
      return;
    }
    if (!this.domainConfig.isPortalChatLiveEnabled()) {
      return;
    }
    if (!this.auth.loggedIn()) {
      return;
    }
    this.started = true;
    this.usePollFallback = false;
    this.reconnectAttempt = 0;
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void this.fetchInbox({ silent: true });
        if (this.usePollFallback) {
          this.schedulePoll();
        }
      } else {
        this.clearPoll();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    void this.fetchInbox({ silent: true });
    this.connectSse();
  }

  stop(): void {
    this.started = false;
    this.usePollFallback = false;
    this.reconnectAttempt = 0;
    this.sseOpenedAt = 0;
    this.clearPoll();
    this.clearReconnect();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.inboxSubject.next({ totalUnreadRequirements: 0, totalUnreadMessages: 0, requirements: [] });
    this.unreadCounts.next(new Map());
  }

  /** Tras marcar leídos en el modal. */
  async refreshAfterRead(): Promise<void> {
    if (!this.started) {
      return;
    }
    await this.fetchInbox({ silent: true });
    this.emitRefresh();
  }

  markRequirementReadLocally(requirementId: number): void {
    const next = new Map(this.unreadCounts.value);
    next.delete(Number(requirementId));
    this.unreadCounts.next(next);
    const inbox = this.inboxSubject.value;
    const requirements = inbox.requirements.filter((r) => r.id !== Number(requirementId));
    this.inboxSubject.next({
      totalUnreadRequirements: requirements.length,
      totalUnreadMessages: requirements.reduce((s, r) => s + (r.unreadMessageCount || 0), 0),
      requirements,
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
    this.eventsSubject.complete();
    this.inboxSubject.complete();
    this.unreadCounts.complete();
  }

  private token(): string {
    return localStorage.getItem('access_token') || '';
  }

  private connectSse(): void {
    if (!this.started || this.usePollFallback) {
      return;
    }
    if (!this.auth.loggedIn()) {
      this.stop();
      return;
    }
    if (this.abortController) {
      this.abortController.abort();
    }
    const ac = new AbortController();
    this.abortController = ac;
    this.sseOpenedAt = 0;

    const url = `${environment.apiUrl}/trackvision/portal-chat/events`;
    fetch(url, {
      method: 'GET',
      headers: {
        Token: this.token(),
        Accept: 'text/event-stream',
      },
      signal: ac.signal,
    })
      .then(async (response) => {
        if (response.status === 401 || response.status === 403) {
          this.stop();
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`SSE HTTP ${response.status}`);
        }
        this.sseOpenedAt = Date.now();
        // Reconciliar unread perdidos durante el gap de desconexión.
        void this.fetchInbox({ silent: true });
        await this.readSseStream(response.body, ac.signal);
        throw new Error('SSE closed');
      })
      .catch((err) => {
        if (ac.signal.aborted || !this.started) {
          return;
        }
        this.scheduleReconnectOrPoll(err);
      });
  }

  private async readSseStream(body: ReadableStream<Uint8Array>, signal: AbortSignal): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';
      for (const chunk of parts) {
        this.handleSseChunk(chunk);
      }
    }
  }

  private handleSseChunk(chunk: string): void {
    const lines = chunk.split('\n');
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (!dataLines.length) {
      return;
    }
    let payload: PortalChatLiveEvent;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch {
      return;
    }
    if (eventName === 'portal.chat.unread_changed' || payload.type === 'portal.chat.unread_changed') {
      this.onUnreadEvent(payload);
    }
  }

  private onUnreadEvent(event: PortalChatLiveEvent): void {
    this.eventsSubject.next(event);
    const reqId = Number(event.requirementId);
    if (reqId) {
      const next = new Map(this.unreadCounts.value);
      const nextCount = unreadMessageBadgeCount(next.get(reqId)) + 1;
      next.set(reqId, nextCount);
      this.unreadCounts.next(next);
    }
    const label = event.requirementLabel || `ID ${event.requirementId}`;
    this.toast.show(`Nuevo mensaje del cliente en ${label}`, {
      classname: 'bg-success text-light',
      delay: 8000,
    });
    void this.fetchInbox({ silent: true });
    this.emitRefresh();
  }

  private scheduleReconnectOrPoll(_err: unknown): void {
    if (this.sseOpenedAt && Date.now() - this.sseOpenedAt >= SSE_STABLE_MS) {
      this.reconnectAttempt = 0;
    }
    this.reconnectAttempt += 1;
    if (this.reconnectAttempt >= 5) {
      this.usePollFallback = true;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.schedulePoll();
      return;
    }
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt - 1)
    );
    this.clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.started && !this.usePollFallback) {
        this.connectSse();
      }
    }, delay);
  }

  private schedulePoll(): void {
    this.clearPoll();
    if (!this.started || !this.usePollFallback || document.visibilityState === 'hidden') {
      return;
    }
    if (!this.auth.loggedIn()) {
      this.stop();
      return;
    }
    this.pollTimer = setTimeout(async () => {
      await this.fetchInbox({ silent: false });
      this.schedulePoll();
    }, POLL_MS);
  }

  private clearPoll(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private async fetchInbox(opts: { silent: boolean }): Promise<void> {
    try {
      if (!this.auth.loggedIn()) {
        this.stop();
        return;
      }
      const response = await fetch(`${environment.apiUrl}/trackvision/portal-chat/unread-inbox`, {
        method: 'GET',
        headers: {
          Token: this.token(),
          Accept: 'application/json',
        },
      });
      if (response.status === 401 || response.status === 403) {
        this.stop();
        return;
      }
      if (response.status === 429) {
        // Rate limit: pasar a poll-only (no duplicar con SSE sano).
        this.usePollFallback = true;
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
        this.clearReconnect();
        // Retry-After puede ser delta-seconds o HTTP-date; Number(date) → NaN.
        const rawRetryAfter = Number(response.headers.get('Retry-After'));
        const retryAfter = Number.isFinite(rawRetryAfter) && rawRetryAfter > 0 ? rawRetryAfter : 60;
        this.clearPoll();
        this.pollTimer = setTimeout(() => this.schedulePoll(), Math.max(retryAfter, 30) * 1000);
        return;
      }
      if (!response.ok) {
        return;
      }
      const body = await response.json();
      const data = (body && body.result) || body;
      const requirements = Array.isArray(data?.requirements) ? data.requirements : [];
      const inbox: PortalChatUnreadInbox = {
        totalUnreadRequirements: Number(data?.totalUnreadRequirements || 0),
        totalUnreadMessages: Number(data?.totalUnreadMessages || 0),
        requirements,
      };
      const prevCounts = this.unreadCounts.value;
      const nextCounts = new Map<number, number>();
      for (const row of requirements) {
        const count = unreadMessageBadgeCount(row.unreadMessageCount);
        // Nunca guardar 0: el badge verde no debe aparecer sin mensajes unread.
        if (count > 0) {
          nextCounts.set(Number(row.id), count);
        }
      }
      this.inboxSubject.next(inbox);
      this.unreadCounts.next(nextCounts);

      if (!opts.silent) {
        for (const [id, count] of nextCounts) {
          const prev = unreadMessageBadgeCount(prevCounts.get(id));
          if (count > prev) {
            const row = requirements.find((r) => Number(r.id) === id);
            this.toast.show(`Nuevo mensaje del cliente en ${row?.name || `ID ${id}`}`, {
              classname: 'bg-success text-light',
              delay: 8000,
            });
            this.emitRefresh();
            break;
          }
        }
      }
    } catch {
      /* ignore transient network errors */
    }
  }

  private emitRefresh(): void {
    this.refreshHandlers.forEach((handler) => {
      try {
        handler();
      } catch {
        /* ignore */
      }
    });
  }
}
